import React, { useState, useEffect, useMemo, useRef } from 'react';
import { hooks } from '../../hooks';
import { text } from '../../text';
import { utils } from '../../utils';
import { theme } from '../../constants';
import { components } from '../../components';
import axiosInstance from '../../services/axiosInstance';
import { RootState } from '../../store';
import { useLanguage } from '../../context/LanguageContext';
import { MdFilterList, MdPendingActions, MdCheckCircle, MdCancel, MdEventNote, MdAttachFile } from 'react-icons/md';
import InfiniteScroll from 'react-infinite-scroll-component';
import { listCache } from '../../utils/listCache';

// Fix for TS2786: 'Icon' cannot be used as a JSX component
const FilterListIcon = MdFilterList as any;
const PendingActionsIcon = MdPendingActions as any;
const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const EventNoteIcon = MdEventNote as any;
const AttachFileIcon = MdAttachFile as any;

type Tab = 'mine' | 'team';

const CACHE_KEY = 'leaves';

export const LeaveHistory: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { t: translate, language } = useLanguage();
  const t = translate.leaveHistory;
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const userPermissions = (authSlice as any).permissions || [];
  const canApproveLeave = userPermissions.includes('leave:approve');

  // Rule 1: lazy init from cache — avoid useEffect restore (would show loading on remount)
  const initialCache = listCache.restore(CACHE_KEY);
  const [activeTab, setActiveTab] = useState<Tab>((initialCache?.meta?.activeTab as Tab) ?? 'mine');
  const [leaves, setLeaves] = useState<any[]>(initialCache?.items ?? []);
  const [isLoading, setIsLoading] = useState(!initialCache);
  const [offset, setOffset] = useState(initialCache?.cursor ?? 0);
  const [hasMore, setHasMore] = useState(initialCache?.hasMore ?? true);

  // Rule 2: skip first offset effect run when restoring from cache
  const isFirstOffsetRun = useRef(true);

  // Rule 3: track scroll via ref so save effect always writes the latest value
  const scrollTopRef = useRef(initialCache?.scrollTop ?? 0);

  const currentYear = new Date().getFullYear().toString();
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<string>(currentYear);
  const [filterName, setFilterName] = useState<string>('');

  const fetchLeaves = async (currentOffset: number, tab: Tab) => {
    try {
      if (currentOffset === 0) setIsLoading(true);

      const response = await axiosInstance.get(`/leaves?limit=10&offset=${currentOffset}&tab=${tab}`);
      const newData = response.data;

      if (newData.length < 10) setHasMore(false);
      else setHasMore(true);

      if (currentOffset === 0) {
        setLeaves(newData);
      } else {
        setLeaves(prev => {
          const existingIds = new Set(prev.map(l => l.id));
          const uniqueNew = newData.filter((l: any) => !existingIds.has(l.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to fetch leave history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save cache on unmount and whenever key state changes
  useEffect(() => {
    return () => {
      listCache.save(CACHE_KEY, { items: leaves, cursor: offset, hasMore, scrollTop: scrollTopRef.current, meta: { activeTab } });
    };
  }, [leaves, offset, hasMore, activeTab]);

  // Initial load — skip if restored from cache
  useEffect(() => {
    if (!initialCache) {
      fetchLeaves(0, activeTab);
    }
  }, []);

  // Load more when offset increases via InfiniteScroll
  // Rule 2: skip the first run when cache was restored (offset may be non-zero)
  useEffect(() => {
    if (isFirstOffsetRun.current) { isFirstOffsetRun.current = false; return; }
    if (offset > 0) {
      fetchLeaves(offset, activeTab);
    }
  }, [offset]);

  const handleTabChange = (tab: Tab) => {
    if (tab === activeTab) return;
    listCache.clear(CACHE_KEY);
    isFirstOffsetRun.current = true;
    scrollTopRef.current = 0;
    setActiveTab(tab);
    setLeaves([]);
    setOffset(0);
    setHasMore(true);
    setFilterName('');
    fetchLeaves(0, tab);
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter((leave) => {
      if (!leave.start_date) return false;
      const leaveDate = new Date(leave.start_date);
      const matchMonth = filterMonth === 'all' || leaveDate.getMonth().toString() === filterMonth;
      const matchYear = filterYear === 'all' || leaveDate.getFullYear().toString() === filterYear;
      const matchName = !filterName.trim() || (leave.user_name || '').toLowerCase().includes(filterName.toLowerCase());
      return matchMonth && matchYear && matchName;
    });
  }, [leaves, filterMonth, filterYear, filterName]);

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { color: '#10B981', bg: '#D1FAE5', icon: <CheckCircleIcon size={16} />, label: t.status.approved };
      case 'rejected': return { color: '#EF4444', bg: '#FEE2E2', icon: <CancelIcon size={16} />, label: t.status.rejected };
      case 'cancelled': return { color: '#6B7280', bg: '#F3F4F6', icon: <CancelIcon size={16} />, label: (t.status as any).cancelled || 'Cancelled' };
      case 'pending':
      default: return { color: '#F59E0B', bg: '#FEF3C7', icon: <PendingActionsIcon size={16} />, label: t.status.pending };
    }
  };

  const getLeaveTypeTranslation = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'paid time off (pto)': return 'Paid Time Off (PTO)';
      case 'sick leave': return translate.leaveRequest.sickLeave;
      case 'unpaid leave': return translate.leaveRequest.unpaidLeave;
      case 'maternity leave': return translate.leaveRequest.maternityLeave;
      case 'marriage leave': return translate.leaveRequest.marriageLeave;
      default: return type;
    }
  };

  const renderHeader = () => (
    <components.Header
      title={t.title}
      goBack={true}
      plus={true}
      onPlus={() => navigate('/LeaveRequest')}
    />
  );

  const renderTabs = () => {
    if (!canApproveLeave) return null;

    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['mine', 'team'] as Tab[]).map((tab) => {
          const isActive = activeTab === tab;
          const label = tab === 'mine' ? (t as any).tabMine : (t as any).tabTeam;
          return (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 10,
                border: isActive ? 'none' : `1px solid #FFEFE6`,
                backgroundColor: isActive ? theme.colors.mainColor : theme.colors.white,
                color: isActive ? theme.colors.white : theme.colors.mainDark,
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: isActive ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderFilters = () => {
    const years = Array.from({length: 5}, (_, i) => (parseInt(currentYear) - 3 + i).toString());

    const selectStyle: React.CSSProperties = {
      width: '100%',
      height: 44,
      padding: '0 14px',
      borderRadius: 10,
      border: '1px solid #FFEFE6',
      backgroundColor: theme.colors.white,
      outline: 'none',
      fontSize: 14,
      fontFamily: 'inherit',
      color: theme.colors.mainDark,
      cursor: 'pointer'
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {activeTab === 'team' && (
          <input
            type="text"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder={(t as any).filterByName}
            style={{ ...selectStyle, width: undefined }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 44,
            height: 44,
            backgroundColor: theme.colors.white,
            borderRadius: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #FFEFE6',
            flexShrink: 0
          }}>
            <FilterListIcon size={24} color={theme.colors.mainDark} />
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              style={selectStyle}
            >
              <option value="all">{t.allMonths}</option>
              {t.months.map((m: string, i: number) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              style={selectStyle}
            >
              <option value="all">{t.allYears}</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => {
    if (isLoading) {
      return <text.T16 style={{ textAlign: 'center', marginTop: 40 }}>{t.loading}</text.T16>;
    }

    if (filteredLeaves.length === 0) {
      return (
        <div style={{ textAlign: 'center', marginTop: 40, opacity: 0.5 }}>
          <text.T16 style={{ marginTop: 10 }}>{t.noData}</text.T16>
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={leaves.length}
        next={() => setOffset(prev => prev + 10)}
        hasMore={hasMore}
        loader={
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{t.loadingMore}</text.T14>
          </div>
        }
        endMessage={
          <div style={{ textAlign: 'center', padding: '10px 0', marginBottom: 20 }}>
            <text.T14 style={{ color: '#6B7280', textAlign: 'center'}}>{t.allDataLoaded}</text.T14>
          </div>
        }
        scrollableTarget="scrollableDiv"
        style={{ overflow: 'visible' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredLeaves.map((leave) => {
          const status = getStatusConfig(leave.status);
          const startDate = new Date(leave.start_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short' });
          const endDate = new Date(leave.end_date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });

          return (
            <div
              key={leave.id}
              onClick={() => {
                // Rule 4: save cache explicitly before navigate — useEffect won't run in time
                listCache.save(CACHE_KEY, { items: leaves, cursor: offset, hasMore, scrollTop: scrollTopRef.current, meta: { activeTab } });
                navigate('/LeaveDetails', { state: { leave } });
              }}
              style={{
                backgroundColor: 'white',
                padding: 16,
                borderRadius: 16,
                border: '1px solid #F3F4F6',
                boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                cursor: 'pointer'
              }}
            >
              <div style={{ ...utils.rowCenterSpcBtw(), marginBottom: 12, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <EventNoteIcon size={20} color={theme.colors.mainDark} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <text.H5 style={{ textTransform: 'capitalize' }}>{getLeaveTypeTranslation(leave.leave_type)}</text.H5>
                    {activeTab === 'team' && leave.user_name && (
                      <text.T14 style={{ color: theme.colors.mainDark, fontWeight: 'bold', fontSize: 13 }}>
                        {leave.user_name}
                      </text.T14>
                    )}
                  </div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 20,
                  backgroundColor: status.bg, color: status.color,
                  fontSize: 12, fontWeight: 'bold', flexShrink: 0
                }}>
                  {status.icon}
                  <span>{status.label}</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, backgroundColor: '#F9FAFB', padding: '6px 12px', borderRadius: 8, marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <text.T14 style={{ color: '#6B7280', fontSize: 12 }}>{t.dates}</text.T14>
                  <text.T14 style={{ color: theme.colors.mainDark, fontSize: 12, fontWeight: '600' }}>{startDate} - {endDate}</text.T14>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <text.T14 style={{ color: '#6B7280', fontSize: 12 }}>{t.duration}</text.T14>
                  <text.T14 style={{ color: theme.colors.mainDark, fontSize: 12, fontWeight: '600' }}>{leave.duration_days} {t.days}</text.T14>
                </div>
              </div>

              <div style={{ padding: '0 4px' }}>
                <text.T14 style={{ color: '#6B7280' }} numberOfLines={2}>
                  {leave.reason}
                </text.T14>
              </div>
              {leave.attachment_url && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    backgroundColor: '#EFF6FF', color: '#3B82F6',
                    borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: '600',
                  }}>
                    <AttachFileIcon size={12} />
                    <span>{(t as any).hasAttachment}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </InfiniteScroll>
    );
  };

  return (
    <div id='screen' style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {renderHeader()}
      <main
        id="scrollableDiv"
        className='container'
        style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}
        onScroll={(e) => { scrollTopRef.current = (e.currentTarget as HTMLElement).scrollTop; }}
        ref={(el) => { if (el && initialCache?.scrollTop) { el.scrollTop = initialCache.scrollTop; } }}
      >
        {renderTabs()}
        {renderFilters()}
        {renderList()}
      </main>
    </div>
  );
};
